import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import DialogActions from "components/Actions/dialogs/components/DialogActions";
import Dialog from "components/DesignComponents/Dialog";
import ParamsContext from "context/params/paramsContext";
import TableContext from "context/table/tableContext";
import { useApi } from "shared/hooks/useApi";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import { maxDialogWidth } from "shared/styles";
import { ENDPOINTS, TABLE_NAMES } from "shared/types/enums";

import { useUpdateDesktopLicense } from "../hooks";
import { DeleteLicenseProps, LicenseData } from "./types";

const DeleteLicense = ({
    data,
    isOpen,
    close,
    isDesktopLicense
}: DeleteLicenseProps) => {
    const { setReloadItems } = useContext(ParamsContext);
    const { detailsData } = useContext(TableContext);

    const { t } = useTranslation();
    const { toMd, fromMd } = useMediaQueries();
    const { deleteData, handleResponse } = useApi();
    const { updateLicenseDetails } = useUpdateDesktopLicense();

    const [isLoading, setIsLoading] = useState(false);

    const [licenseData, setLicenseData] = useState<LicenseData>({
        id: null,
        name: null
    });

    useEffect(() => {
        if (data) {
            const { id, name } = data;

            setLicenseData({
                id,
                name
            });
        }
    }, [isOpen, data]);

    const { id, name } = licenseData;

    const handleSubmit = async () => {
        try {
            setIsLoading(true);

            const response = await deleteData(
                isDesktopLicense
                    ? ENDPOINTS.DesktopLicenses
                    : ENDPOINTS.OfflineLicenses,
                id!
            );

            close();

            if (isDesktopLicense) {
                setReloadItems(TABLE_NAMES.DesktopLicenses);
            } else {
                updateLicenseDetails(detailsData.id);
            }

            handleResponse(response);
        } catch (error) {
            handleResponse(error);
        }

        setIsLoading(false);
    };

    return (
        <Dialog
            isActionsSeparator
            title={t(
                `General##delete ${isDesktopLicense ? "desktop" : "offline"} license`
            )}
            description={t(
                `Dialog##${isDesktopLicense ? "desktop" : "offline"} license delete description`,
                {
                    name
                }
            )}
            open={isOpen}
            close={close}
            submit={handleSubmit}
            slotProps={{
                transition: {
                    onExited: () => {
                        setLicenseData({
                            id: null,
                            name: null
                        });
                    }
                }
            }}
            actions={
                <DialogActions
                    onClose={close}
                    isLoading={isLoading}
                    isReadyToConfirm={id !== null && name}
                    confirmationButtonText={t("Button##delete")}
                />
            }
            css={css(fromMd && maxDialogWidth)}
            extendToMaxWidth={toMd}
        />
    );
};

export default DeleteLicense;
